import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Lock, Mail, Eye, EyeOff, Save, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function AccountSettings() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Email change form
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  
  // Name change form
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await api.auth.me();
      setUser(userData);
      setFullName(userData.full_name || '');
      setNewEmail(userData.email || '');
    } catch (err) {
      console.error(err);
      toast.error('Failed to load account settings');
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordForm = () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return false;
    }
    if (!newPassword) {
      toast.error('Please enter a new password');
      return false;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return false;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return false;
    }
    return true;
  };

  const validateEmailForm = () => {
    if (!newEmail) {
      toast.error('Please enter a new email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (newEmail.toLowerCase() !== confirmEmail.toLowerCase()) {
      toast.error('Email addresses do not match');
      return false;
    }
    if (!emailPassword) {
      toast.error('Please enter your current password to change email');
      return false;
    }
    if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error('New email must be different from current email');
      return false;
    }
    return true;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;
    
    setSaving(true);
    try {
      // Verify current password first
      const verifyResponse = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          password: currentPassword
        })
      });
      
      if (!verifyResponse.ok) {
        toast.error('Current password is incorrect');
        setSaving(false);
        return;
      }
      
      // Change password via API
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      
      if (response.ok) {
        toast.success('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to change password');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!validateEmailForm()) return;
    
    setSaving(true);
    try {
      // Verify password and change email via API
      const response = await fetch('/api/auth/change-email', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          newEmail,
          password: emailPassword
        })
      });
      
      if (response.ok) {
        toast.success('Email changed successfully. Please use your new email to log in next time.');
        setUser({ ...user, email: newEmail });
        setNewEmail(newEmail);
        setConfirmEmail('');
        setEmailPassword('');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to change email');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to change email');
    } finally {
      setSaving(false);
    }
  };

  const handleNameChange = async () => {
    if (!fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    
    if (fullName === user?.full_name) {
      toast.error('Name is unchanged');
      return;
    }
    
    setSaving(true);
    try {
      await api.auth.updateMe({ full_name: fullName.trim() });
      setUser({ ...user, full_name: fullName.trim() });
      toast.success('Name updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update name');
    } finally {
      setSaving(false);
    }
  };

  const PasswordInput = ({ type, value, onChange, placeholder, showToggle, onToggle }) => (
    <div className="relative">
      <Input
        type={showToggle ? 'text' : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-10"
      />
      {showToggle !== undefined && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={onToggle}
        >
          {showToggle ? (
            <EyeOff className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Eye className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Account Settings</h1>
        <p className="text-slate-500 mt-1">
          Manage your account credentials and security settings
        </p>
      </div>

      <Tabs defaultValue="password" className="space-y-6">
        <TabsList>
          <TabsTrigger value="password">
            <Lock className="w-4 h-4 mr-2" />
            Password
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="name">
            <User className="w-4 h-4 mr-2" />
            Display Name
          </TabsTrigger>
        </TabsList>

        {/* Password Change Tab */}
        <TabsContent value="password">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-emerald-500" />
                  Change Password
                </CardTitle>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <PasswordInput
                      type="password"
                      value={currentPassword}
                      onChange={setCurrentPassword}
                      placeholder="Enter your current password"
                      showToggle={showCurrentPassword}
                      onToggle={() => setShowCurrentPassword(!showCurrentPassword)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <PasswordInput
                      type="password"
                      value={newPassword}
                      onChange={setNewPassword}
                      placeholder="Enter a new password"
                      showToggle={showNewPassword}
                      onToggle={() => setShowNewPassword(!showNewPassword)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Must be at least 6 characters
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <PasswordInput
                      type="password"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      placeholder="Confirm your new password"
                      showToggle={showConfirmPassword}
                      onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                    />
                    {confirmPassword && newPassword && (
                      <div className="flex items-center gap-1 text-sm">
                        {confirmPassword === newPassword ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span className="text-emerald-600">Passwords match</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-600">Passwords do not match</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Email Change Tab */}
        <TabsContent value="email">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Change Email Address
                </CardTitle>
                <CardDescription>
                  Update your email address. You'll need to verify your current password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Current Email:</strong> {user?.email}
                  </p>
                </div>

                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newEmail">New Email Address</Label>
                    <Input
                      id="newEmail"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter your new email address"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmEmail">Confirm New Email</Label>
                    <Input
                      id="confirmEmail"
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      placeholder="Confirm your new email"
                    />
                    {confirmEmail && newEmail && (
                      <div className="flex items-center gap-1 text-sm">
                        {confirmEmail.toLowerCase() === newEmail.toLowerCase() ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span className="text-emerald-600">Email addresses match</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-600">Email addresses do not match</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emailPassword">Current Password (Required)</Label>
                    <Input
                      id="emailPassword"
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      placeholder="Enter your current password to confirm"
                    />
                    <p className="text-xs text-muted-foreground">
                      We require your current password to change your email address
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={saving || !newEmail || !confirmEmail || !emailPassword}
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing Email...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Change Email
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Name Change Tab */}
        <TabsContent value="name">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-500" />
                  Display Name
                </CardTitle>
                <CardDescription>
                  Update the name that appears throughout the application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>

                <Button
                  onClick={handleNameChange}
                  disabled={saving || !fullName.trim() || fullName === user?.full_name}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Name
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
